import '.p{position:absolute;}.ssr-plugin'
import '.dq{top:0px;}.ssr-plugin'
import '.z{bottom:0px;}.ssr-plugin'
import '.dp{left:0px;}.ssr-plugin'
import '.dr{right:0px;}.ssr-plugin'
import { styled } from 'inlines'
import { useUpdate } from '../../hooks/useUpdate.js'
import { useSize } from '../../hooks/useSize.js'
import { ScrollArea } from '../ScrollArea/index.js'
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { PartialOptions } from 'overlayscrollbars'

export type Pagination = {
  type: 'scroll' | 'button'
  total?: number
  onScroll?: (p: {
    x: number
    y: number
    width: number
    height: number
    top: number
    left: number
    pageIndex: number
    pageSize: number
    first: number
    last: number
    actualFirst: number
    actualLast: number
  }) => void
  onPageChange?: (p: {
    index: number
    pageSize: number
    start: number
    end: number
  }) => Promise<void>
  onPageLoad?: (p: {
    index: number
    pageSize: number
    start: number
    end: number
  }) => void
  loadMore?: (p: {
    index: number
    pageSize: number
    start: number
    end: number
  }) => Promise<void>
  onPreloadPage?: (p: {
    index: number
    pageSize: number
    start: number
    end: number
  }) => void
  preloadRatio?: number
}

export type VirtualizedRenderer = (p: {
  values: any[]
  isLoading?: boolean
  loading: boolean
  itemHeight: number
  itemWidth: number
  start: number
  index: number
  end: number
  width: number
  pageCount: number
  total: number
}) => React.ReactNode

export type ItemSize =
  | number
  | ((size: {
      width: number
      height: number
    }) => { width?: number; height: number; rows?: number } | number)

export type VirtualizedProps = {
  pagination: Pagination
  values: any[]
  itemSize: ItemSize
  isBlock?: boolean
  isLoading?: boolean
  scrollbars?: PartialOptions['scrollbars']
  onViewportChange?: (el: HTMLDivElement | null) => void
  children: VirtualizedRenderer
  onSelectValues?: (values: any[]) => any[]
  onScroll?: React.DOMAttributes<HTMLDivElement>['onScroll']
}

export type VirtualizedResetScroll = {
  resetScroll: () => void
}

const Virtualized = forwardRef<VirtualizedResetScroll, VirtualizedProps>(
  (props, ref) => {
    const internalRef = useRef<{
      checksum: number
      pageCount?: number
      currentIndex?: number
      lastPreloadIndex?: number
      p?: string
      start: number
      end: number
      pageSize?: number
      pagination?: Pagination
      loading: boolean
      loaded: number
      values: any[]
      itemHeightProp: ItemSize
      itemHeight: number
      itemWidth: number
      width: number
      height: number
      rows: number
      lastPageIndex: number
      lastPageSize: number

      onScrollProp?: VirtualizedProps['onScroll']
    }>({
      checksum: 0,
      start: 0,
      end: 0,
      width: 0,
      height: 0,
      pageCount: 0,
      currentIndex: 0,
      lastPreloadIndex: 0,
      rows: 1,
      loading: false,
      loaded: -1,
      values: [],
      itemHeight: 0,
      itemWidth: 0,
      itemHeightProp: props.itemSize,
      lastPageIndex: 0,
      lastPageSize: 0,
      onScrollProp: props.onScroll,
    })

    const update = useUpdate()

    const loadedRef = useRef(false)

    const lastTotalRef = useRef<number | undefined>(props.pagination?.total)
    const lastItemSizeRef = useRef<ItemSize>(props.itemSize)

    internalRef.current.pagination = props.pagination
    internalRef.current.itemHeightProp = props.itemSize
    internalRef.current.onScrollProp = props.onScroll

    if (typeof props.itemSize === 'number') {
      if (internalRef.current.itemHeight !== props.itemSize) {
        internalRef.current.itemHeight = props.itemSize
      }
    }

    const applyItemSize = (size: { width: number; height: number }) => {
      let shouldUpdate = false

      if (typeof internalRef.current.itemHeightProp === 'function') {
        const calc = internalRef.current.itemHeightProp(size)

        if (typeof calc === 'number') {
          if (calc !== internalRef.current.itemHeight) {
            internalRef.current.itemHeight = calc
            shouldUpdate = true
          }
        } else if (calc && typeof calc === 'object') {
          const nextHeight = calc.height
          const nextWidth =
            typeof calc.width === 'number'
              ? calc.width
              : internalRef.current.itemWidth
          const nextRows = calc.rows ?? internalRef.current.rows

          if (nextHeight !== internalRef.current.itemHeight) {
            internalRef.current.itemHeight = nextHeight
            shouldUpdate = true
          }

          if (nextWidth !== internalRef.current.itemWidth) {
            internalRef.current.itemWidth = nextWidth
            shouldUpdate = true
          }

          if (nextRows !== internalRef.current.rows) {
            internalRef.current.rows = nextRows
            shouldUpdate = true
          }
        }
      }

      return shouldUpdate
    }

    const updateBlock = useCallback((index: number) => {
      internalRef.current.currentIndex = index
      internalRef.current.start = Math.max(
        index * (internalRef.current.pageCount || 0) * internalRef.current.rows,
        0,
      )

      internalRef.current.end = Math.min(
        (index + 2) *
          (internalRef.current.pageCount || 0) *
          internalRef.current.rows,
        internalRef.current.pagination?.total,
      )

      if (
        !internalRef.current.pageCount ||
        (index === internalRef.current.lastPageIndex &&
          internalRef.current.pageCount === internalRef.current.lastPageSize)
      ) {
        return
      }

      internalRef.current.lastPageIndex = index
      internalRef.current.lastPageSize = internalRef.current.pageCount

      internalRef.current.pagination?.onPageChange?.({
        index,
        pageSize: internalRef.current.pageCount,
        start: internalRef.current.start,
        end: internalRef.current.end,
      })

      update()
    }, [])

    const scrollRef = useSize((size) => {
      const { height, width } = size

      internalRef.current.width = width
      internalRef.current.height = height

      const shouldUpdate = applyItemSize(size)

      const itemH = internalRef.current.itemHeight || 1
      const n = Math.ceil(height / itemH)

      if (n !== internalRef.current.pageCount) {
        internalRef.current.pageCount = n
        internalRef.current.pageSize = n * itemH

        updateBlock(0)

        if (!loadedRef.current && internalRef.current.pageCount) {
          loadedRef.current = true
          internalRef.current.pagination?.onPageLoad?.({
            index: 0,
            pageSize: internalRef.current.pageCount,
            start: internalRef.current.start,
            end: internalRef.current.end,
          })
        }
      } else if (shouldUpdate) {
        update()
      }
    })

    if (lastItemSizeRef.current !== props.itemSize) {
      lastItemSizeRef.current = props.itemSize
      internalRef.current.itemHeightProp = props.itemSize

      if (typeof props.itemSize === 'function') {
        const { width, height } = internalRef.current
        if (width && height) {
          const shouldUpdate = applyItemSize({ width, height })

          if (shouldUpdate) {
            const itemH = internalRef.current.itemHeight || 1
            const n = Math.ceil(height / itemH)

            if (n !== internalRef.current.pageCount) {
              internalRef.current.pageCount = n
              internalRef.current.pageSize = n * itemH
              updateBlock(0)

              if (!loadedRef.current && internalRef.current.pageCount) {
                loadedRef.current = true
                internalRef.current.pagination?.onPageLoad?.({
                  index: 0,
                  pageSize: internalRef.current.pageCount,
                  start: internalRef.current.start,
                  end: internalRef.current.end,
                })
              }
            } else {
              update()
            }
          }
        }
      }
    }

    const total = internalRef.current.pagination?.total

    if (lastTotalRef.current !== total) {
      lastTotalRef.current = total
      const nextEnd = Math.min(
        ((internalRef.current.currentIndex || 0) + 2) *
          (internalRef.current.pageCount || 0) *
          internalRef.current.rows,
        total,
      )

      if (nextEnd !== internalRef.current.end) {
        internalRef.current.end = nextEnd
      }

      if (internalRef.current.loaded > total) {
        internalRef.current.loaded = total
      }
    }

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const y = event.currentTarget.scrollTop
        const x = event.currentTarget.scrollLeft
        const total = internalRef.current.pagination.total
        const pageHeight =
          internalRef.current.pageCount * internalRef.current.itemHeight
        const pageIndex = pageHeight ? Math.floor(y / pageHeight) : 0

        if (internalRef.current.pagination.onPreloadPage) {
          const ratio = internalRef.current.pagination.preloadRatio ?? 0.75
          const inside = y - pageIndex * pageHeight

          if (
            pageHeight &&
            inside >= pageHeight * ratio &&
            internalRef.current.lastPreloadIndex !== pageIndex + 1
          ) {
            internalRef.current.lastPreloadIndex = pageIndex + 1
            const start =
              (pageIndex + 1) *
              internalRef.current.pageCount *
              internalRef.current.rows
            const end = Math.min(
              (pageIndex + 3) *
                internalRef.current.pageCount *
                internalRef.current.rows,
              total,
            )
            internalRef.current.pagination.onPreloadPage({
              index: pageIndex + 2,
              pageSize: internalRef.current.pageCount,
              start,
              end,
            })
          }
        }

        if (internalRef.current.pagination.loadMore) {
          if (
            event.currentTarget.scrollHeight -
              y -
              event.currentTarget.clientHeight <=
              0 &&
            !internalRef.current.loading &&
            internalRef.current.loaded < total
          ) {
            internalRef.current.loading = true
            update()

            internalRef.current.pagination
              .loadMore({
                index: pageIndex,
                pageSize: internalRef.current.pageCount,
                start: internalRef.current.start,
                end: internalRef.current.end,
              })
              .finally(() => {
                if (internalRef.current.loaded < total) {
                  internalRef.current.loaded = total
                }
                internalRef.current.loading = false
                updateBlock(pageIndex)
              })
          }
        }

        if (internalRef.current.pagination.onScroll) {
          const { clientHeight, clientWidth, clientLeft, clientTop } =
            event.currentTarget

          const count = internalRef.current.pageCount
          const itemH = internalRef.current.itemHeight || 1
          const actualFirst = Math.ceil(y / itemH)
          const actualLast = Math.min(total, actualFirst + count)
          const buffer = Math.floor(count / 3)
          const first = Math.max(0, actualFirst - buffer)
          const last = Math.min(total, actualLast + buffer)

          internalRef.current.pagination.onScroll({
            x,
            y,
            width: clientWidth,
            height: clientHeight,
            top: clientTop,
            left: clientLeft,
            pageIndex,
            pageSize: count,
            first,
            last,
            actualFirst,
            actualLast,
          })
        }

        if (internalRef.current.currentIndex !== pageIndex) {
          updateBlock(pageIndex)
        }

        internalRef.current.onScrollProp?.(event)
      },
      [],
    )

    if (internalRef.current.pageSize) {
      internalRef.current.values = props.isBlock
        ? props.values
        : props.values.slice(internalRef.current.start, internalRef.current.end)

      if (props.onSelectValues) {
        internalRef.current.values = props.onSelectValues(
          internalRef.current.values,
        )
      }
    }

    useImperativeHandle(ref, () => ({
      resetScroll: () => {
        internalRef.current.checksum += 1
        updateBlock(0)
        update()
      },
    }))

    return (
      <ScrollArea
        key={internalRef.current.checksum}
        ref={scrollRef}
        onViewportChange={props.onViewportChange}
        scrollbars={props.scrollbars}
        style={{
          flexGrow: 1,
          maxHeight: '100%',
          width: '100%',
          maxWidth: '100%',
          position: 'relative',
        }}
        onScroll={handleScroll}
      >
        <div className="p dq z dp dr"
          style={{
            height:
              (internalRef.current.pagination.total *
                internalRef.current.itemHeight) /
                internalRef.current.rows +
              (internalRef.current.pagination.loadMore
                ? internalRef.current.itemHeight
                : 0), // add bottom with load more
          }}
        >
          <div
            style={{
              transform: `translate(0,${
                internalRef.current?.currentIndex *
                  internalRef.current?.pageSize || 0
              }px)`,
              width:
                internalRef.current.itemWidth &&
                internalRef.current.itemWidth > 0
                  ? internalRef.current.itemWidth
                  : '100%',
            }}
          >
            {internalRef.current.itemHeight
              ? props.children({
                  width: internalRef.current.width,
                  isLoading: props.isLoading,
                  loading: internalRef.current.loading,
                  values: internalRef.current.values,
                  itemHeight: internalRef.current.itemHeight,
                  itemWidth: internalRef.current.itemWidth,
                  total: internalRef.current.pagination.total,
                  start: internalRef.current.start,
                  end: internalRef.current.end,
                  pageCount: internalRef.current.pageCount,
                  index: internalRef.current.currentIndex,
                })
              : null}
          </div>
        </div>
      </ScrollArea>
    )
  },
)

const MemoizedVirtualized = memo(Virtualized)
export { MemoizedVirtualized as Virtualized }
